// src/pages/PesquisaJurisprudencia/hooks/useFiltrosTab.js
import { useState, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '../utils/constants';

const useFiltrosTab = ({ onResultados, onPaginacao, onResetBusca }) => {
  const [tribunaisSelecionados, setTribunaisSelecionados] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [textoFiltro, setTextoFiltro] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [modalTagsAberto, setModalTagsAberto] = useState(false);
  const [estatisticas, setEstatisticas] = useState(null);

  const carregarTags = useCallback(async () => {
    if (tribunaisSelecionados.length === 0) { setTags([]); return; }
    setLoadingTags(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      tribunaisSelecionados.forEach(t => params.append('tribunais', t));
      const response = await fetch(`${API_BASE_URL}/jurisprudencia/tags?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setTags(response.ok ? (await response.json()).data || [] : []);
    } catch (e) {
      console.error('Erro ao carregar tags:', e);
      setTags([]);
    } finally {
      setLoadingTags(false);
    }
  }, [tribunaisSelecionados]);

  const carregarEstatisticas = useCallback(async () => {
    if (tribunaisSelecionados.length === 0) { setEstatisticas(null); return; }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      tribunaisSelecionados.forEach(t => params.append('tribunais', t));
      const response = await fetch(`${API_BASE_URL}/jurisprudencia/estatisticas?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setEstatisticas(response.ok ? (await response.json()).data : null);
    } catch (e) {
      console.error('Erro ao carregar estatísticas:', e);
      setEstatisticas(null);
    }
  }, [tribunaisSelecionados]);

  useEffect(() => {
    if (tribunaisSelecionados.length > 0) {
      carregarTags();
      carregarEstatisticas();
    } else {
      setTags([]);
      setEstatisticas(null);
    }
  }, [tribunaisSelecionados, carregarTags, carregarEstatisticas]);

  const handleTribunalChange = useCallback((tribunal) => {
    setTribunaisSelecionados(prev => {
      const novos = prev.includes(tribunal)
        ? prev.filter(t => t !== tribunal)
        : [...prev, tribunal];
      setSelectedTags([]);
      setTextoFiltro('');
      onResetBusca();
      return novos;
    });
  }, [onResetBusca]);

  const handleTagsChange = useCallback((newTags) => setSelectedTags(newTags), []);

  const handleRemoveTag = useCallback((tag) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  }, []);

  const pesquisar = useCallback(async (page = 1) => {
    if (tribunaisSelecionados.length === 0) {
      alert('Selecione pelo menos um tribunal para pesquisa');
      return;
    }
    if (selectedTags.length === 0 && !textoFiltro.trim()) {
      alert('Selecione pelo menos uma tag ou digite texto para pesquisa');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      tribunaisSelecionados.forEach(t => params.append('tribunais', t));
      selectedTags.forEach(tag => params.append('tags', tag));
      if (textoFiltro.trim()) params.append('texto', textoFiltro.trim());
      params.append('page', page.toString());
      params.append('limit', '20');
      const response = await fetch(`${API_BASE_URL}/jurisprudencia/search?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        onResultados(data.data || []);
        onPaginacao(data.pagination || {});
      } else {
        alert('Erro ao realizar pesquisa');
      }
    } catch (e) {
      console.error('Erro na pesquisa:', e);
      alert('Erro ao realizar pesquisa');
    } finally {
      setLoading(false);
    }
  }, [tribunaisSelecionados, selectedTags, textoFiltro, onResultados, onPaginacao]);

  const getState = useCallback(() => ({
    tribunaisSelecionados,
    tags,
    selectedTags,
    textoFiltro,
    estatisticas
  }), [tribunaisSelecionados, tags, selectedTags, textoFiltro, estatisticas]);

  const restoreState = useCallback((estado) => {
    setTribunaisSelecionados(estado.tribunaisSelecionados || []);
    setTags(estado.tags || []);
    setSelectedTags(estado.selectedTags || []);
    setTextoFiltro(estado.textoFiltro || '');
    setEstatisticas(estado.estatisticas || null);
  }, []);

  return {
    tribunaisSelecionados,
    tags,
    selectedTags,
    textoFiltro,
    setTextoFiltro,
    loading,
    loadingTags,
    modalTagsAberto,
    setModalTagsAberto,
    estatisticas,
    handleTribunalChange,
    handleTagsChange,
    handleRemoveTag,
    pesquisar,
    carregarTags,
    carregarEstatisticas,
    getState,
    restoreState
  };
};

export default useFiltrosTab;